/**
 * batch-58 profiel-match (FO-429) - de DTO's tussen de API en de backoffice voor
 * de zachte profielwens-hint in het toewijzingspad.
 *
 * De rekenkern zelf staat in `@streep/utils` (`berekenProfielMatch`); hier staat
 * alleen de vorm waarin de score over de lijn gaat. Dat scheelt de backoffice
 * een `@streep/utils`-import in client-componenten en houdt het contract expliciet.
 *
 * Zichtbaarheid: dit is een INTERNE planner-hint. Er gaat niets van naar
 * `apps/klant`; de klant ziet alleen zijn eigen profielwens (bestaand, FO-413-D8),
 * nooit welke monteurs er overwogen zijn.
 */

import { z } from 'zod'

export type ProfielMatchCriteriumSoortDTO = 'VAARDIGHEID' | 'CERTIFICAAT'

export type ProfielMatchCriteriumStatusDTO =
  | 'VOLDAAN'
  | 'VERLOOPT_BINNENKORT'
  | 'VERLOPEN'
  | 'ONTBREEKT'

export interface ProfielMatchCriteriumDTO {
  soort: ProfielMatchCriteriumSoortDTO
  id: string
  naam: string
  voldaan: boolean
  status: ProfielMatchCriteriumStatusDTO
  /** Vervaldatum (ISO) van het geldigste certificaat; null bij vaardigheden. */
  verlooptOp: string | null
  /** Korte NL-toevoeging, bv. `verlopen op 1 feb. 2026`. */
  toelichting: string | null
}

/** De uitkomst voor één monteur. */
export interface ProfielMatchDTO {
  employeeId: string
  gedekt: number
  gewenstTotaal: number
  criteria: ProfielMatchCriteriumDTO[]
  /** Deelverzameling van `criteria` die niet voldaan is (voedt "mist: ..."). */
  ontbrekend: ProfielMatchCriteriumDTO[]
}

/** Het antwoord van `GET /planning/profiel-match/:clientId`. */
export interface ProfielMatchContextDTO {
  clientId: string
  klantNaam: string | null
  /** false = geen (of leeg) profiel; de UI toont dan NIETS (spec 129 §3). */
  heeftProfiel: boolean
  /** Vrije toelichting van de klant bij zijn profiel. */
  toelichting: string | null
  /** De door de klant gekozen criteria met hun naam, in zijn eigen volgorde. */
  gewenst: Array<{ soort: ProfielMatchCriteriumSoortDTO; id: string; naam: string }>
  /** Per monteur de uitkomst; leeg wanneer `heeftProfiel` false is. */
  matches: ProfielMatchDTO[]
}

/**
 * Query van `GET /planning/profiel-match/:clientId`.
 *
 * `employeeIds` is bewust OPTIONEEL en comma-gescheiden: het planbord vraagt om
 * één monteur, de picker laat het weg en krijgt alle medewerkers die op de
 * peildatum in dienst zijn. Een URL met 500 id's zou de header-limiet raken;
 * daarom is "alles" de picker-modus en niet een lange lijst.
 */
export const profielMatchQuerySchema = z.object({
  employeeIds: z.string().trim().min(1).optional(),
  /** Inzetdatum (yyyy-mm-dd) waarop de certificaat-geldigheid beoordeeld wordt. */
  peildatum: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik het formaat jjjj-mm-dd')
    .optional(),
})

export type ProfielMatchQuery = z.infer<typeof profielMatchQuerySchema>
